<template lang="pug">
FetchErrorTransition(:isFetching="isPending" :error="error")
  UpsertBooking(
    v-if="booking"
    :booking="booking"
    :products="productsWithChildResourceKeys"
    :resources="groupedResources"
    :classifications="classifications"
    @delete="deleteBookingProduct"
    @deleteDeal="deleteDeal"
    @switchNonScheduledProduct="switchProduct"
    )
    template(slot-scope="{ index }")
      CancelSaveEdit(
        @cancel="cancel"
        @save="trySave"
        @edit="editSchedule"
        :isSaving="isSaving"
        :canEditSchedule="canEditSchedule"
        :index="index"
        )
  WarningModal(
    :quantityMismatch="quantityMismatch"
    :segmentMismatch="segmentMismatch"
    :conflictingScheduleValidation="conflictingScheduleValidation"
    :booking="booking"
    :resources="resources"
    v-show="showWarningModal" 
    @close="showWarningModal = false" 
    @saveBooking="validateAndSave"
    )
  p.not-found(v-if="!booking") Booking not found
</template>

<script>
import {
  forEach,
  pipe,
  prop,
  propOr,
  propEq,
  map,
  find,
  reduce,
  assoc,
  filter,
  sortBy,
  pathOr,
  both,
  concat,
  any,
  values,
  groupBy,
  toPairs,
} from 'ramda'
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import UpsertBooking from './UpsertBooking.vue'
import CancelSaveEdit from './CancelSaveEdit.vue'
import WarningModal from './WarningModal.vue'
import BookingUpsertModel from './models/BookingUpsertModel.js'
import {
  anySegmentlessQuantityMismatch,
  anySegmentQuantityMismatch,
} from './util/numberOfQuantitiesHelper.js'
import BookingUpsertService from './BookingUpsertService.js'
import validate from './upsertModelValidator.js'
import Result from 'folktale/result'
import BusinessFeatureEnum from '@v/models/BusinessFeatureEnum.js'
import { upsertBooking } from '@v/bookings/edit/saveBooking.js'
import { validateBookingProducts } from '@v/bookings/edit/validateBookingProducts.js'
import { makeObj, propIsTruthy } from '@v/util/briqRamdaExt.js'
import { setSegmentlessBools } from './setSegmentlessBools.js'
import { completeQuantities } from '@v/bookings/edit/util/completeBookingProductSegmentQuantities.js'
import { sumSegmentQuantities } from '@v/bookings/edit/util/bookingDealFunctions.js'
import filterAndSortProducts from '@v/bookings/edit/util/filterAndSortProducts.js'

const matcher = f => pipe(prop('value'), f)

export default {
  name: 'UpsertBookingWrapper',
  components: { UpsertBooking, CancelSaveEdit, WarningModal },
  props: { bookingKey: String },
  data() {
    return {
      upsertService: null,
      legacyError: null,
      response: null,
      isSaving: false,
      bookingProductKeysToDelete: [],
      showWarningModal: false,
      quantityMismatch: false,
      segmentMismatch: false,
      conflictingScheduleValidation: {
        isValid: true,
      },
      isNumberOfQuantitesPerSegmentInProductsEqual: true,
      isNumberOfQuantitesOfSegmentlessProductsEqual: true,
    }
  },
  computed: {
    ...mapGetters('bookingUpsert', [
      'booking',
      'isPending',
      'products',
      'resources',
      'classifications',
      'hasDeletedProducts',
      'flatSegments',
      'bookingDeals',
    ]),
    ...mapState('bookingUpsert', [
      'fetchError',
      'fetchError',
      'saveError',
      'editModelWasFetched',
      'forEditorModel', // for debug
    ]),
    ...mapGetters('briqState', ['businessUnitHasFeature', 'businessUnitAdditionalFeatures']),
    error() {
      const noProductsError = this.products?.length ? null : 'No products'
      return this.legacyError || this.fetchError || noProductsError
    },
    filteredAndSortedProducts() {
      return filterAndSortProducts(this.products)
    },
    groupedResources() {
      return reduce((a, n) => assoc(n.Key, n, a), {})(this.resources || [])
    },
    productsWithChildResourceKeys() {
      const getChildResourceKeys = p =>
        pipe(
          find(propEq('Key', p.ResourceKey)),
          propOr([], 'ChildResources'),
          map(prop('Key')),
        )(this.resources)
      return map(p => ({
        ...p,
        childResourceKeys: getChildResourceKeys(p),
      }))(this.filteredAndSortedProducts || [])
    },
    canEditSchedule() {
      return (
        this.businessUnitHasFeature(BusinessFeatureEnum.BookingEditSchedule) &&
        !!this.booking?.Key &&
        !this.hasDeletedProducts
      )
    },
  },
  methods: {
    ...mapActions('bookingUpsert', ['maybeFetchForEditorModel']),
    ...mapMutations('bookingUpsert', ['removeBookingDeal']),

    // Fetch methods
    handleResult(fetchResult) {
      this.upsertService
        .getResultHandler(this.$window.location.search)(
          this.products,
          this.classifications,
        )(fetchResult)
        .matchWith({
          Ok: matcher(this.handleValidation),
          Error: ({ value }) => {
            this.$notifyError('Something went wrong')
            this.$logError(value)
            this.legacyError = value
          },
        })
    },

    handleValidation(validation) {
      validation.matchWith({
        Success: matcher(this.setState),
        Failure: ({ value }) => {
          this.$notifyError('Invalid query parameters')
          this.$logError('validation failed: ', value.join())
          this.logFailures(value)
          this.legacyError = value
        },
      })
    },

    setState({ booking }) {
      this.$store.commit('bookingUpsert/setBooking', booking)
    },

    // Events
    cancel() {
      if (this.bookingKey) this.gotoViewBooking(this.bookingKey)
      else this.$setLocationPath('planboard')
    },

    editSchedule() {
      this.$setLocationPath(`planboard?legacy&booking=${this.bookingKey}&edit=1`)
    },

    deleteBookingProduct(bookingProduct) {
      this.booking.BookingProducts = this.booking.BookingProducts.filter(p => {
        if (bookingProduct.Key) return p.Key !== bookingProduct.Key
        else
          return !(
            p.ProductKey === bookingProduct.ProductKey &&
            p.ProductGroupKey === bookingProduct.ProductGroupKey
          )
      })
      if (bookingProduct.Key) this.bookingProductKeysToDelete.push(bookingProduct.Key)
    },

    deleteDeal(dealKey) {
      this.bookingProductKeysToDelete = pipe(
        prop('BookingProducts'),
        filter(both(propEq('ProductGroupKey', dealKey), propIsTruthy('Key'))),
        map(prop('Key')),
        concat(this.bookingProductKeysToDelete),
      )(this.booking)
      this.removeBookingDeal(dealKey)
    },

    switchProduct({ oldBookingProduct, newProductKey }) {
      const newChosenProduct = find(propEq('Key', newProductKey))(this.products)
      const newBookingProduct = {
        Key: null,
        ProductKey: newChosenProduct.Key,
        ProductName: newChosenProduct.NameInternal,
        NeedsToBeScheduled: newChosenProduct.NeedsToBeScheduled,
        ResourceKey: newChosenProduct.ResourceKey,
        ProductActiveState: 'Active', // needed to remove warning styling for inactive/deleted products
        ProductQuantity: 0,
        QuantitiesPerClassification: [],
        isSegmentless: !newChosenProduct.ActiveSegments?.length,
        tempId: new Date().getTime(),
      }
      newBookingProduct.QuantitiesPerClassification = completeQuantities(
        newChosenProduct,
        this.flatSegments,
        newBookingProduct,
      )
      const matchBookingProduct = bp => {
        if (oldBookingProduct.Key && bp.Key) return oldBookingProduct.Key === bp.Key
        if (oldBookingProduct.tempId && bp.tempId) return oldBookingProduct.tempId === bp.tempId
        else return oldBookingProduct.ProductKey === bp.ProductKey
      }
      if (oldBookingProduct.Key) this.bookingProductKeysToDelete.push(oldBookingProduct.Key)
      this.booking.BookingProducts = map(bp => (matchBookingProduct(bp) ? newBookingProduct : bp))(
        this.booking.BookingProducts,
      )
    },

    async trySave() {
      if (this.isSaving) return
      if (splitProductQuantitiesAreInvalid(this.bookingDeals)) {
        this.$notifyError('Product quantities per segment do not add up to deal quantities')
        return
      }
      const upsertModel = this.buildUpsertModel()

      this.quantityMismatch = anySegmentlessQuantityMismatch(this.booking)
      this.segmentMismatch = anySegmentQuantityMismatch(this.booking)
      if (this.businessUnitAdditionalFeatures.warnOnConflictingBookingProducts) {
        this.isSaving = true
        this.conflictingScheduleValidation = await validateBookingProducts(this.$apiClient)(
          upsertModel,
        )
        this.isSaving = false
      }

      if (
        this.quantityMismatch ||
        this.segmentMismatch ||
        !this.conflictingScheduleValidation.isValid
      ) {
        this.showWarningModal = true
      } else this.validateAndSave()
    },

    async validateAndSave() {
      const upsertModel = this.buildUpsertModel()
      this.isSaving = true
      const result = await validate(upsertModel).matchWith({
        Success: async ({ value }) => {
          return await upsertBooking(this.$apiClient)(value)
        },
        Failure: ({ value }) =>
          Promise.resolve(
            Result.Error({
              errorMessage: value,
              userMessage: `Invalid booking: ${value[0]}`,
            }),
          ),
      })
      result.matchWith({
        Ok: matcher(v => {
          this.$notifySuccess(v.successMessage)
          this.gotoViewBooking(upsertModel.Key || v.response.data)
        }),
        Error: matcher(messages => {
          if (messages.errorMessage) this.$logError(messages.errorMessage)
          this.$notifyError(messages.userMessage)
        }),
      })
      this.isSaving = false
    },

    buildUpsertModel() {
      const customer = this.getCustomer()
      return new BookingUpsertModel({
        booking: this.booking,
        bookingProductsToDelete: this.bookingProductKeysToDelete,
        customer,
      })
    },

    // todo: refactor using a Vue3 ref
    getCustomer() {
      let customer = null
      const isNewBooking = !this.bookingKey
      const withNewCustomer = !this.booking.CustomerKey
      if (isNewBooking && withNewCustomer) {
        this.$bus.$emit('getNewCustomer', newCustomer => (customer = newCustomer))
      }
      return customer
    },

    async upsert(upsertModel) {
      this.isSaving = true
      const result = await this.upsertService.upsert(upsertModel)
      result.matchWith({
        Ok: matcher(v => {
          this.$notifySuccess(v.successMessage)
          this.gotoViewBooking(upsertModel.key || v.response.data)
        }),
        Error: matcher(messages => {
          if (messages.errorMessage) this.$logError(messages.errorMessage)
          this.$notifyError(messages.userMessage)
        }),
      })
      this.isSaving = false
    },

    // other
    gotoViewBooking(bookingKey) {
      this.$setLocationPath(`bookings/${bookingKey}/view`)
    },

    logFailures(validation) {
      return forEach(e => this.$logWarning(e), validation)
    },
  },

  async beforeMount() {
    await this.maybeFetchForEditorModel(this.bookingKey)
    this.upsertService = new BookingUpsertService()
    this.products.forEach(p => {
      const resource = this.resources.find(propEq('Key', p.ResourceKey))
      if (resource) {
        p.childResourceKeys = resource.ChildResources?.map(prop('Key')) || [] // needed for joinProduct in mapJsonParamToBookingProduct
      }
    })
    if (!this.bookingKey) {
      this.handleResult(
        Result.Ok({
          booking: {
            Key: null,
            Number: null,
            StartTime: null,
            BookingProducts: [],
            NumberOfPersons: 0,
            NumberOfPersonsPerClassification: [],
            CustomerKey: null,
          },
        }),
      )
    } else {
      const mapBooking = makeObj({
        Key: prop('Key'),
        Number: prop('Number'),
        StartTime: prop('StartTime'),
        BookingProducts: prop('BookingProducts'),
        NumberOfPersons: prop('NumberOfPersons'),
        NumberOfPersonsPerClassification: prop('NumberOfPersonsPerClassification'),
        CustomerKey: pathOr(null, ['MainBooker', 'Key']),
      })

      pipe(prop('Booking'), mapBooking, setSegmentlessBools(this.products), x =>
        this.handleResult(Result.Ok({ booking: x })),
      )(this.forEditorModel)
    }
  },
}

// Utility

const getBookingDealSegmentQuantitiesFromSplitProducts = pipe(
  prop('scheduledProducts'),
  groupBy(prop('ProductGroupProductKey')),
  values,
  filter(x => x.length > 1), // only split bookingproducts remain
  map(sumSegmentQuantities), // [{ [segmentCode]: quantity }]
)

const splitProductQuantitiesAreInvalid = any(bd => {
  const splitGroups = getBookingDealSegmentQuantitiesFromSplitProducts(bd)
  return any(
    pipe(
      toPairs,
      any(([code, quantity]) =>
        bd.quantities.perSegment[code] !== undefined
          ? quantity !== bd.quantities.perSegment[code]
          : false,
      ),
    ),
    splitGroups,
  )
})
</script>

<style lang="stylus" scoped>
.not-found
  margin-top 30px
  font-size 36px
  display flex
  align-items center
</style>
